import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { add, format, parse } from "date-fns";
import { useCallback, useState } from "react";
import { SharedValue } from "react-native-reanimated";
import { useFragment } from "react-relay";

import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import { MobileHeader } from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { usePatronTopbarMobileHeaderNav } from "../../../../components/PageWrapper/hooks/usePatronTopbarMobileHeaderNav";
import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { FacilityLogo } from "../../../Session/components/FacilityLogo";
import { DurationBasedFiltersModal } from "../DurationBasedFiltersModal";
import { DurationBasedAgendaHeaderFragment_facility$key } from "./__generated__/DurationBasedAgendaHeaderFragment_facility.graphql";
import { DurationBasedAgendaHeaderFragment_session$key } from "./__generated__/DurationBasedAgendaHeaderFragment_session.graphql";
import { useGetDurationBasedAgendaFilterBarValues } from "./hooks/useGetDurationBasedAgendaFilterBarValues";

type Props = {
  facilityRef: DurationBasedAgendaHeaderFragment_facility$key | null;
  sessionRef: DurationBasedAgendaHeaderFragment_session$key | null;
  scrollY?: SharedValue<number>;
  clubWeekStartsOn: NumericalDayOfWeek;
};
export const DurationBasedAgendaHeader = ({
  facilityRef,
  sessionRef,
  scrollY,
  clubWeekStartsOn,
}: Props) => {
  const [isAgendaFilterModalOpen, setAgendaFilterModalOpen] = useState(false);
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const isMobile = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    lg: false,
    xl: false,
  });

  const session = useFragment(
    DurationBasedAgendaHeaderSessionFragmentString,
    sessionRef
  );

  const isSignedIn = !!session?.currentClientProfile?.member?.id;

  const facility = useFragment(
    DurationBasedAgendaHeaderFragmentString,
    facilityRef
  );
  const clubName = facility?.club?.name || "";
  const agendaFilterBarValues = useGetDurationBasedAgendaFilterBarValues(
    facility ?? null
  );

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate("Home", { screen: "MainHome" });
    }
  };

  const onClose = useCallback(() => {
    setAgendaFilterModalOpen(false);
  }, [setAgendaFilterModalOpen]);

  const facilityName = facility?.displayNameOrFacilityName ?? "";

  const today = format(new Date(), "yyyy-MM-dd");
  const memberBookingWindowDays = facility?.memberBookingWindowDays ?? 14;
  const endDate = format(
    add(parse(today, "yyyy-MM-dd", new Date()), {
      days: memberBookingWindowDays,
    }),
    "yyyy-MM-dd"
  );
  const selectedDate = route?.params?.date ?? today;
  const handleDateChange = useCallback(
    (date: string) => {
      if (date !== selectedDate) {
        navigation.setParams({ date });
      }
    },
    [navigation, selectedDate]
  );

  const { customNavigationBar, navigationBarVariant } =
    usePatronTopbarMobileHeaderNav({
      leftAction: isSignedIn ? "Back" : null,
      onLeftActionClick: isSignedIn ? handleBackPress : undefined,
      title: clubName,
      subTitle: t`Book a time`,
      clubLogoImageUrl: getThumbnail(
        facility?.club?.logoImage?.publicId ?? "",
        40,
        40
      )?.toURL(),
    });

  return (
    <>
      {isMobile ? (
        <MobileHeader
          customNavigationBar={customNavigationBar}
          navigationBarVariant={navigationBarVariant}
          filtersBar={{
            title: {
              leadingText: t`Find`,
              boldText: facilityName,
              trailingText: t`Times`,
            },
            values: agendaFilterBarValues,
            onPress: () => setAgendaFilterModalOpen(true),
          }}
          dateSwitcher={{
            startDate: today,
            endDate,
            selectedDate,
            onChange: handleDateChange,
            clubWeekStartsOn,
          }}
          scrollY={scrollY}
        />
      ) : (
        <ScreenHeader
          isRounded={true}
          scrolled={true}
          title={t`Book a time`}
          subHeading={clubName}
          variant="REGULAR"
          titleRightElement={
            <FacilityLogo
              clubRef={facility?.club ?? null}
              imageProps={{
                style: { height: 56, width: 56, borderRadius: 24 },
              }}
            />
          }
          leftItem={
            isSignedIn ? (
              <Button
                accessibilityLabel={t`Back button`}
                variant="secondary"
                leftIcon={<ArrowLeftIcon />}
                onPress={handleBackPress}
                testID="mobile-header-back-button"
              />
            ) : null
          }
        />
      )}
      {isMobile ? (
        <DurationBasedFiltersModal
          isOpen={isAgendaFilterModalOpen}
          onClose={onClose}
          facilityRef={facility ?? null}
          sessionRef={session ?? null}
        />
      ) : null}
    </>
  );
};

const DurationBasedAgendaHeaderFragmentString = graphql`
  fragment DurationBasedAgendaHeaderFragment_facility on Facility {
    ...useGetDurationBasedAgendaFilterBarValues_facility
    ...DurationBasedFiltersModalFragment_facility
    name
    displayNameOrFacilityName
    memberBookingWindowDays
    club {
      name
      logoImage {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
      ...FacilityLogoFragment_club
    }
  }
`;

const DurationBasedAgendaHeaderSessionFragmentString = graphql`
  fragment DurationBasedAgendaHeaderFragment_session on Session {
    ...DurationBasedFiltersModal_session
    currentClientProfile {
      member {
        ... on Customer {
          id
        }
        ... on Member {
          id
        }
      }
    }
  }
`;
